import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Calculator, TrendingDown, TrendingUp, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const fmt = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

/**
 * Refi impact dialog — shown when the user clicks a rate in the interest-rates
 * widget. Re-prices every loan at the target rate (using remaining balance +
 * remaining term) and ranks by monthly savings.
 */
export default function RefiImpactDialog({ open, rate, rateLabel, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || rate === null || rate === undefined) return;
    setLoading(true);
    setErr(null);
    setData(null);
    api.get("/loans/refi-impact", { params: { rate } })
      .then(({ data }) => setData(data))
      .catch((e) => {
        const msg = formatApiError(e?.response?.data?.detail) || "Failed to load";
        setErr(msg);
        toast.error(msg);
      })
      .finally(() => setLoading(false));
  }, [open, rate]);

  const summary = data?.summary;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-3xl" data-testid="refi-impact-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-slate-500" />
            What if you refinanced at {rate?.toFixed?.(2)}%?
          </DialogTitle>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mt-1">
            Target: {rateLabel || "Market rate"} · {rate?.toFixed?.(2)}%
          </div>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-sm text-slate-400" data-testid="refi-impact-loading">Crunching the numbers…</div>
        ) : err ? (
          <div className="py-10 text-center text-sm text-red-600" data-testid="refi-impact-error">{err}</div>
        ) : !data || data.rows.length === 0 ? (
          <div className="py-10 text-center" data-testid="refi-impact-empty">
            <Calculator className="w-5 h-5 text-slate-300 mx-auto mb-2" />
            <div className="text-sm text-slate-600">Add a loan to see refinance impact.</div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Headline */}
            <div className={`rounded-sm border p-4 flex items-start gap-3 ${summary.saving_loans > 0 ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`} data-testid="refi-impact-headline">
              <Sparkles className={`w-5 h-5 shrink-0 ${summary.saving_loans > 0 ? "text-emerald-600" : "text-slate-400"}`} />
              <div className="flex-1">
                {summary.saving_loans > 0 ? (
                  <>
                    <div className="text-sm font-bold text-slate-900">
                      {summary.saving_loans} of {summary.total_loans} loans could save you money
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      Total savings: <span className="font-mono font-bold text-emerald-700">{fmt(summary.total_savings_monthly)}/mo</span>
                      {" · "}
                      <span className="font-mono font-bold text-emerald-700">{fmt(summary.total_savings_annual)}/yr</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm font-bold text-slate-900">No savings at this rate</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Your existing loans are already better than today's {rate?.toFixed?.(2)}%. Keep them.
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Per-loan rows */}
            <div className="rounded-sm border border-slate-200 overflow-hidden">
              <table className="w-full text-sm" data-testid="refi-impact-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-2 text-left overline text-slate-500">Loan</th>
                    <th className="px-4 py-2 text-right overline text-slate-500">Current</th>
                    <th className="px-4 py-2 text-right overline text-slate-500">New</th>
                    <th className="px-4 py-2 text-right overline text-slate-500">Δ / month</th>
                    <th className="px-4 py-2 text-right overline text-slate-500">Δ / year</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => {
                    const saves = r.delta_monthly > 0;
                    const tone = saves ? "text-emerald-700" : (r.delta_monthly < 0 ? "text-red-600" : "text-slate-500");
                    const Icon = saves ? TrendingDown : (r.delta_monthly < 0 ? TrendingUp : null);
                    return (
                      <tr key={r.loan_id} className="border-b last:border-b-0 border-slate-100" data-testid={`refi-impact-row-${r.loan_id}`}>
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-slate-900 truncate max-w-[220px]">{r.loan_number}</div>
                          <div className="text-xs text-slate-500 truncate max-w-[220px]">{r.property_address}</div>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-0.5">
                            {r.current_rate.toFixed(2)}% · {Math.round(r.remaining_months / 12)}y left · {r.loan_type === "interest_only" ? "IO" : "P&I"}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-slate-700">{fmt(r.current_monthly)}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-slate-900 font-semibold">{fmt(r.new_monthly)}</td>
                        <td className={`px-4 py-2.5 text-right font-mono font-bold ${tone}`}>
                          <span className="inline-flex items-center gap-1 justify-end">
                            {Icon && <Icon className="w-3 h-3" />}
                            {r.delta_monthly === 0 ? "—" : fmt(r.delta_monthly)}
                          </span>
                        </td>
                        <td className={`px-4 py-2.5 text-right font-mono ${tone}`}>
                          {r.delta_annual === 0 ? "—" : fmt(r.delta_annual)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="text-xs text-slate-400">
              Estimates use remaining balance + remaining term of each loan at the target rate.
              Excludes origination fees, points, and prepayment penalties — consult your lender before acting.
            </div>
          </div>
        )}

        <DialogFooter>
          <button type="button" className="btn-primary-dark" onClick={onClose} data-testid="refi-impact-close">
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
